/**
 * Timestamp formatting, shared by both consoles.
 *
 * `UI_GUIDELINES.md` §3.11 standardises on **relative time with the absolute
 * UTC value on hover**, and a live review (vista#789 / vista#792) found the apps
 * split down the middle: Devices, WAN/Uplink and Host Health rendered relative,
 * while Overview, Alerts, Settings and the whole admin console rendered
 * absolute. Mixed formats across pages make two timestamps incomparable at a
 * glance, which is the point of the rule.
 *
 * The formatting lives here, in the React-free package, for the reason
 * vista#671 established: it was already duplicated once (`fmtRelative` in
 * `ProxyMacTable.jsx` and `DeviceL2Panel.jsx`, consolidated under vista#433)
 * and the admin console had no copy at all, which is how it ended up absolute
 * everywhere. The `<RelativeTime>` component itself stays per app — it is six
 * lines of JSX over these two functions, and this package deliberately holds no
 * JSX.
 */

/**
 * "18m ago". Coarse by design: a data-freshness display is read at a glance,
 * and seconds of precision past the first minute is noise.
 *
 * Returns an em dash for a missing or unparseable value rather than "Invalid
 * Date" or "NaN ago" — an absent timestamp is a normal state (a device never
 * seen, an invitation never accepted) and it should read as absent.
 */
export function formatRelative(iso, now = Date.now()) {
  if (!iso) return '—';
  const then = new Date(iso).getTime();
  if (!Number.isFinite(then)) return '—';
  // Clamped at zero: a clock skew between the agent and the browser must not
  // render "in 3 minutes" on a freshness display.
  const diff = Math.max(0, Math.floor((now - then) / 1000));
  if (diff < 60) return `${diff}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

/**
 * `2026-03-26 14:32:11 UTC` — the tooltip §3.11 requires beside every relative
 * value.
 *
 * UTC rather than the browser's zone, and explicitly suffixed. An operator
 * comparing a dashboard against an agent log or a syslog line is comparing
 * against UTC, and a local-time tooltip silently shifts the comparison by the
 * viewer's offset — the one thing a tooltip on a timestamp exists to settle.
 */
export function formatUtc(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (!Number.isFinite(d.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return (
    `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())} ` +
    `${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())}:${pad(d.getUTCSeconds())} UTC`
  );
}

/**
 * Is this timestamp older than `seconds`?
 *
 * For the stale/down emphasis §10.2 asks for. Kept here so "how old is too
 * old" is answered the same way on both consoles.
 */
export function isOlderThan(iso, seconds, now = Date.now()) {
  if (!iso) return false;
  const then = new Date(iso).getTime();
  if (!Number.isFinite(then)) return false;
  return now - then > seconds * 1000;
}
